<template>
<div id="sheet-add">
  <debug-add :target="vm"></debug-add>
  <div v-if="filling">
    <alert type="warning">
      Remplissage de la fiche
    </alert>
  </div>
  <div v-else-if="surveillance != null" class="row">
    <div v-if="status.current === status.ready">
      <save-modal v-model="saveModal"
                  :sheet="sheet"
                  :surveillance="surveillance"
                  @close="saveModalClosed"
                  @saved="saved">
      </save-modal>
      <div class="col-xs-12">
        <p class="sheet-status pull-right">
          <span :class="`label label-${sheetStatus.color}`">
            <span :class="`glyphicon glyphicon-${sheetStatus.icon}`"
                  aria-hidden="true">
            </span>
            {{ sheetStatus.text }}
          </span>
        </p>
      </div>
      <div class="col-xs-12">
        <preview :sheet="sheet"
           :cursor="cursor"
           :steps="referenceSteps"
           :notes="notes"
           :state="state"
           :config="config.sheet"
           @commit="commit($event, true)"
           @select="select($event)" />
      </div>
      <div v-if="!state.list && !saveModal && !config.sheet.cheatsheet">
        <span v-for="(s, i) in shortcuts"
              :key="`sheet-add-shortcut-${i}`"
              v-shortkey.once.prevent="s.keys"
              @shortkey="s.action(s.param)">
        </span>
      </div>
    </div>
    <div v-else-if="status.current === status.loading"
         class="col-xs-8 col-xs-offset-2"
         id="sheet-loading">
      <alert type="info">
        <progress-bar
          striped
          :value="100"
          label
          label-text="Chargement de la fiche..."
          active />
      </alert>
    </div>
    <div v-else-if="status.current === status.error"
         class="col-xs-8 col-xs-offset-2"
         id="sheet-error">
      <alert type="danger">
        <p>
          <strong>
            Attention ! Une erreur s'est produite pendant le chargement
            de la fiche :
          </strong>
        </p>
        <p>
          Erreur de connectivité : merci de vérifier votre
          connexion internet avant de réessayer. Si le problème persiste,
          veuillez contacter l'administrateur du site.
        </p>
      </alert>
    </div>
  </div>
</div>
</template>
<script>
import saveModal from '@/sui/components/sheet/add/saveModal'
import preview from '@/sui/components/sheet/add/preview'

import modules from '@/sui/data/modules'
import denominators from '@/sui/data/sheets/edit/denominators'
import toolsUsage from '@/sui/data/sheets/edit/toolsUsage'
import notes from '@/sui/data/sheets/edit/notes'

import debugAdd from '@/sui/components/sheet/add/debugAdd'
import clone from 'clone'

import assert from '@/sui/tools/assert'
import { confirm } from '@/sui/tools/modal'

export default {
  name: 'sheet-add',
  props: {
    config: {
      type: Object,
      required: true
    },

    state: {
      type: Object,
      required: true
    },

    year: {
      type: String,
      required: true
    },

    surveillance: {
      type: Object,
      required: true
    },

    sheets: {
      type: Array,
      required: true
    },

    type: {
      type: String,
      required: true
    },

    saveReminderTimer: {
      type: Number
    },

    sid: {
      type: String
    }
  },

  data: function () {
    return {
      autofill: {},
      filling: false,
      steps: null,
      referenceSteps: null,
      sheet: {},
      stack: [],
      step: {},
      nextItemId: 0,
      saveModal: false,
      saveRequired: false,
      saveReminderPopUpDisplay: false,
      timeoutID: null,
      status: {
        current: null,
        sheet: null,

        ready: 0,
        loading: 1,
        error: 2
      },
      shortcuts: [
        {
          keys: {
            b: ['shift', 'tab']
          },
          action: this.previous
        },
        {
          keys: {
            b: ['tab']
          },
          action: this.next
        }
      ]
    }
  },

  computed: {
    vm: function () {
      return this
    },

    it: function () {
      const count = this.stack.length
      if (count === 0) {
        return null
      }
      return this.stack[count - 1]
    },

    sheetStatus: function () {
      const valid = this.status.sheet === 'valid'
      return {
        icon: valid ? 'ok' : 'pencil',
        text: valid ? 'FICHE VALIDÉE' : 'FICHE EN COURS DE SAISIE',
        color: valid ? 'success' : 'primary'
      }
    },

    cursor: function () {
      if (this.it == null) {
        return null
      }

      return { target: this.it.target, field: this.step.field }
    },

    notes: function () {
      if (this.referenceSteps == null || this.surveillance == null) {
        return {}
      }

      return notes.buildNotes(
        this.referenceSteps, this.sheet, this.surveillance
      )
    }
  },

  watch: {
    sheets: function () {
      this.selectFirstStep()
    },

    surveillance: function (value, old) {
      this.selectFirstStep()
    },

    sid: function (value, old) {
      if (old !== value) {
        this.selectFirstStep()
      }
    },

    timeoutID: function () {
      if (this.saveReminderTimer != null &&
          this.timeoutID == null) {
        this.launchSaveReminderPopupTimer()
      }
    },

    saveRequired: function (value, old) {
      if (!old && value && this.timeoutID == null && this.saveReminderTimer > 0) {
        this.launchSaveReminderPopupTimer()
      }
    },

    'state.list': function (v) {
      if (v) {
        this.stopSaveReminderPopupTimer()
        return
      }
      if (this.saveRequired) {
        this.launchSaveReminderPopupTimer()
      }
    }
  },

  methods: {
    toggleOptions: function () {
      if (this.$refs.dropdown != null) {
        this.$refs.dropdown.toggle()
      }
    },

    reset: function () {
      this.status.sheet = null
      this.status.current = null
      this.steps = null
      this.referenceSteps = null
      this.sheet = {}
      this.stack = []
      this.step = {}
      this.nextItemId = 0
      this.saveModal = false
      this.saveRequired = false
      this.saveReminderPopUpDisplay = null
      this.stopSaveReminderPopupTimer()
    },

    edit: function (prefix) {
      const sheet = this.$store.getters[`${prefix}/edit`]
      if (sheet != null && sheet.errors != null &&
          sheet.errors.length === 0) {
        this.sheet = sheet.sheet
        this.status.sheet = this.$store.state[prefix].edit.st
        this.selectType(this.sheet.type)
        this.purge()
      } else {
        this.status.current = this.status.error
      }
    },

    selectFirstStep: function () {
      if (this.surveillance != null) {
        this.reset()
        const st = this.type
        assert.notNull(st)
        if (st === 'denominators') {
          this.status.current = this.status.loading
          const request = { s: this.surveillance.id, y: this.surveillance.year }
          const vm = this
          this.$store.dispatch('denominators/edit', request).then(
            (response) => {
              if (response.data.err == null) {
                const year = response.data.y
                if (year !== request.y) {
                  confirm(
                    this,
                    `<h3>Fiche Établissement</h3>
<p class="text-justify">
  L'établissement «<strong>${this.surveillance.establishment.name}</strong>»
  était déjà inscrit en ${year} à la surveillance SPIADI.
</p>
<p>
  <strong>
    Souhaitez-vous pré-remplir la Fiche Établissement avec les données de la
    fiche ${year} ? 
  </strong>
</p>
`
                  ).then(() => {
                    vm.edit('denominators')
                    vm.saveRequired = true
                  }).catch(() => {
                    vm.$store.commit('denominators/empty', request.y)
                    vm.edit('denominators')
                  })
                } else {
                  vm.$store.commit(
                    'surveillances/setSingleSheetStatus',
                    {
                      id: vm.surveillance.id,
                      st: response.data.v.st,
                      year: vm.surveillance.year,
                      type: 'establishment'
                    }
                  )
                  vm.edit('denominators')
                }
              } else {
                vm.edit('denominators')
              }

              return response
            }).catch(
            e => {
              // console.log(e)
              this.status.current = this.status.error
            }
          )
        } else if (st === 'toolsUsage') {
          const request = { s: this.surveillance.id, y: this.surveillance.year }
          const vm = this

          this.$store.dispatch('toolsUsage/edit', request).then(
            (response) => {
              if (response.data.err == null) {
                const data = response.data
                if (data != null) {
                  vm.edit('toolsUsage')
                  vm.$store.commit(
                    'surveillances/setSingleSheetStatus',
                    {
                      id: vm.surveillance.id,
                      st: response.data.v.st,
                      year: vm.surveillance.year,
                      type: 'toolsUsage'
                    }
                  )
                }
              } else {
                vm.edit('toolsUsage')
              }
            }
          ).catch(
            e => {
              // console.log(e)
              this.status.current = this.status.error
            }
          )
        } else {
          const sid = this.sid
          if (sid == null) {
            this.selectType(st, true)
            if (this.status.current === this.status.ready) {
              this.next()
            }
          } else {
            this.status.current = this.status.loading
            const request = {
              id: sid,
              t: st,
              y: this.surveillance.year
            }
            const vm = this
            this.$store.dispatch('sheet/edit', request).then((response) => {
              if (response.data.err == null) {
                vm.edit('sheet')
              } else {
                this.status.current = this.status.error
              }
            }).catch(e => {
              // console.log(e)
              this.status.current = this.status.error
            })
          }
        }
      }
    },

    selectReference: function (e) {
      if (e === 'denominators') {
        this.referenceSteps = denominators[this.year]
        return
      }

      if (e === 'toolsUsage') {
        this.referenceSteps = toolsUsage[this.year]
        return
      }

      const module = modules[this.year][e]
      assert(
        module != null && module.sheets != null && module.sheets.edit != null,
        `selectReference: invalid sheet type (${e})`
      )
      this.referenceSteps = clone(module.sheets.edit)
    },

    selectType: function (t, init) {
      this.selectReference(t)
      if (this.referenceSteps == null) {
        this.status.current = this.status.error
        return
      }
      this.steps = this.referenceSteps.fields
      this.initializeFields(this.steps, this.sheet)
      assert(this.stack.length === 0)
      this.stack.length = 0
      this.stack.push({
        steps: this.steps,
        index: -1,
        item: -1,
        target: this.sheet
      })

      if (init) {
        this.sheet.type = t
        this.sheet.year = this.year
      }
      this.status.current = this.status.ready
    },

    initializeFields: function (steps, target) {
      assert(steps != null && target != null)

      const stack = [{ steps: steps, target: target }]
      while (stack.length > 0) {
        const it = stack[0]
        for (let i in it.steps) {
          const step = it.steps[i]
          if (step.initialize != null) {
            step.initialize(this)
          }

          if (step.type === 'array') {
            let items = it.target[step.field]
            if (items != null) {
              items.splice(items.length, 0, {})
            } else {
              items = [{}]
              this.$set(it.target, step.field, items)
            }

            for (let i in items) {
              const subitem = items[i]
              this.$set(subitem, 'id', this.newId())
              stack.push({
                steps: step.config.fields,
                target: subitem
              })
            }
          }
        }

        stack.shift()
      }
    },

    purge: function () {
      const stack = [{ steps: this.steps, target: this.sheet }]
      while (stack.length > 0) {
        const it = stack[0]
        for (let i in it.steps) {
          const step = it.steps[i]

          if (step.type === 'array') {
            const items = it.target[step.field]
            assert(items.length > 0)
            if (step.ignore != null && step.ignore(it.target, this.sheet)) {
              items.splice(1)

              const first = items[0]
              this.resetTarget(first)
              this.initializeFields(step.config.fields, first)
            } else {
              for (let i in items) {
                const subitem = items[i]
                stack.push({
                  steps: step.config.fields,
                  target: subitem
                })
              }
            }
          } else if (it.target[step.field] != null &&
                     step.ignore != null &&
                     step.ignore(it.target, this.sheet)) {
            this.$delete(it.target, step.field)
          }
        }

        stack.shift()
      }
    },

    resetTarget: function (target) {
      assert.notNull(target)

      const vm = this
      Object.keys(target).forEach(
        function (k) {
          if (k !== 'id') {
            vm.$delete(target, k)
          }
        }
      )
    },

    commit: function (event, p) {
      if (this.config.sheet.cheatsheet) {
        return
      }

      const target = this.it ? this.it.target : null
      const e = event.e
      if (target != null && this.step.pop && target[this.it.field] !== e) {
        this.resetTarget(target)
        this.initializeFields(this.it.steps, target)
      }

      if (this.step.commit != null) {
        this.step.commit(this, e, target)
      } else {
        this.$set(this.it.target, this.step.field, e)
      }

      this.saveRequired = true
      if (p) {
        this.purge()
      }

      if (event.goToNextStep) {
        this.next()
      }
    },

    showSaveReminderPopup: function () {
      if (!this.saveRequired || this.saveReminderPopUpDisplay) {
        if (!this.saveReminderPopupDisplay) {
          this.timeoutID = null
        }
        return
      }

      this.saveReminderPopUpDisplay = true
      const minutes = Math.round(this.saveReminderTimer / 60000)
      confirm(
        this,
        `<div>
           <p>
             Il est recommandé d'enregistrer régulièrement la fiche afin d'éviter
             de perdre les champs saisies. Vous pourrez continuer votre saisie après la sauvegarde.
           </p>
         </div>
         <h4 style='margin-bottom: 0px;'>Souhaitez vous enregistrer maintenant ?</h4>`,
        `Vous n'avez pas sauvegardé la fiche depuis ${minutes} minutes !`

      ).then(() => {
        this.save()
        this.saveRequired = false
        this.saveReminderPopUpDisplay = false
      }).catch(() => {
        this.saveReminderPopUpDisplay = false
        this.timeoutID = null
      })
    },

    launchSaveReminderPopupTimer: function () {
      assert(this.saveReminderTimer > 0)
      const vm = this
      this.timeoutID = setTimeout(
        () => { vm.showSaveReminderPopup() },
        this.saveReminderTimer
      )
    },

    stopSaveReminderPopupTimer: function () {
      const id = this.timeoutID
      if (id != null) {
        clearTimeout(id)
      }
    },

    select: function (e) {
      assert.notNull(e)

      const step = e.next ? this.next : this.previous
      while ((e.target !== this.cursor.target ||
              e.field !== this.cursor.field) && step()) {
      }
    },

    popCurrentItem: function () {
      assert(
        this.it.index === 0, `popCurrentItem: wrong index ${this.it.index}`
      )
      assert(
        this.stack.length > 0, 'popCurrentItem: stack should not be empty'
      )

      this.stack = this.stack.slice(0, -1)

      const step = this.it.steps[this.it.index]
      const target = this.it.target[step.field]
      if (this.it.item !== target.length - 1) {
        this.it.index--
      }

      if (this.it.item === target.length - 1) {
        const subitem = { id: target[this.it.item].id }
        this.initializeFields(step.config.fields, subitem)
        this.$set(target, this.it.item, subitem)
        this.it.item = -1
      } else {
        target.splice(this.it.item, 1)
      }
    },

    newId: function () {
      return this.nextItemId++
    },

    newItem: function () {
      return { id: this.newId() }
    },

    enterArray: function (step, previous) {
      assert(step != null && previous != null)

      let target = this.it.target[step.field]
      if (this.it.item === -1) {
        this.it.item = previous ? target.length - 1 : 0
      }

      if (this.it.item === target.length) {
        let item = this.newItem()
        this.initializeFields(step.config.fields, item)
        target.splice(target.length, 0, item)
      }

      const index = previous ? step.config.fields.length - 1 : 0
      this.stack.push({
        steps: step.config.fields,
        index: index,
        target: target[this.it.item],
        item: -1
      })

      return this.it.steps[index]
    },

    ignoreStep: function (step, target) {
      assert.notNull(step)
      return step.readOnly ||
             (step.ignore != null && step.ignore(target, this.sheet))
    },

    enterStep: function (backward) {
      assert.notNull(backward)

      let step = this.it.steps[this.it.index]
      while (step.type === 'array' && !this.ignoreStep(step, this.it.target)) {
        step = this.enterArray(step, backward)
      }
      return step
    },

    next: function () {
      let step = this.step
      do {
        if (step.pop && step.pop(this.it.target, this.sheet)) {
          this.popCurrentItem()
        }

        if (this.it.index + 1 === this.it.steps.length) {
          if (this.stack.length === 1) {
            this.step = {}
            this.it.index = -1
            this.it.item = -1
            return false
          }

          this.stack = this.stack.slice(0, -1)
          this.it.item++
        } else {
          this.it.index++
        }

        step = this.enterStep(false)
      } while (this.ignoreStep(step, this.it.target))

      assert.notNull(step)
      this.step = step

      return true
    },

    previous: function () {
      let step = null
      do {
        if (this.it.index < 0) {
          this.it.index = this.it.steps.length - 1
        } else if (this.it.index === 0) {
          if (this.stack.length === 1) {
            this.step = {}
            this.it.index = -1
            this.it.item = -1
            return false
          }

          this.stack = this.stack.slice(0, -1)
          if (this.it.item > 0) {
            this.it.item--
          } else {
            this.it.item = -1
            this.it.index--
          }
        } else {
          this.it.index--
        }

        step = this.enterStep(true)
      } while (this.ignoreStep(step, this.it.target))

      assert.notNull(step)
      this.step = step
      return true
    },

    save: function () {
      if (this.status.current !== this.status.ready || this.saveModal) {
        return
      }

      assert.notNull(this.referenceSteps)
      if (this.saveRequired) {
        this.saveReminderPopUpDisplay = false
        this.saveModal = true
      } else {
        this.$notify({
          type: 'warning',
          title: 'Aucune modification.s détectée.s sur la fiche.',
          content: `Il n'est pas nécessaire de sauvegarder.`
        })
      }
    },

    saved: function (route) {
      this.saveRequired = false
      this.$notify({
        type: 'success',
        title: `Enregistrement réussi !`,
        content: `La fiche a bien été enregistrée.`
      })

      if (route != null) {
        this.$router.push(route)
      }

      if (this.type !== 'denominators' && this.type !== 'toolsUsage') {
        this.$store.dispatch(
          'sheet/load', {
            s: this.surveillance.id,
            t: this.type,
            y: this.surveillance.year
          }
        )
      } else {
        this.selectFirstStep()
      }
    },

    saveModalClosed: function (reload) {
      this.saveModal = false
      if (reload) {
        this.selectFirstStep()
      }
    },

    checkRoute: function (to, from, next) {
      if (this.saveRequired) {
        this.stopSaveReminderPopupTimer()
        confirm(
          this,
          `<h5><strong>Les modifications sur la fiche n'ont pas été
sauvegardées</strong></h5>
<p>Si vous quittez la page maintenant, toutes les modifications seront
perdues.</p>
<p>Êtes vous sûr.e de vouloir continuer ?</p>`
        ).then(() => {
          this.reset()
          next()
        }).catch(() => {
          if (!this.state.list) {
            this.launchSaveReminderPopupTimer()
          }
          next(false)
        })
      } else {
        this.reset()
        next()
      }
    }
  },

  mounted: function () {
    this.selectFirstStep()
  },

  components: {
    'preview': preview,
    'save-modal': saveModal,
    'debug-add': debugAdd
  }
}
</script>
<style scoped lang="less">
#sheet-add {
    #sheet-loading {
        margin-top: 5rem;
        margin-bottom: 5rem;
        .progress {
            margin-bottom: 0px;
        }
    }
    .sheet-status {
        border-radius: 0.25rem;
        .glyphicon {
            margin-right: 0.2rem;
        }
    }
}
</style>
